import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import assessmentActions from '../../actions/AssessmentActions';
import assessmentReader from '../../store/readHelpers/AssessmentReader';
import i18nService, { t } from '../../services/i18nService';
import QTIAssessmentItem from '../question/QTIAssessmentItem';
import questionHelperServices from '../../services/questionView/QuestionHelperServices';
import questionReader from '../../store/readHelpers/QuestionReader';
import responseService from '../../services/ResponseService';
import sessionReader from '../../store/readHelpers/SessionReader';
import settingsReader from '../../store/readHelpers/SettingsReader';
import stimulusHighlightsActions from '../../actions/StimulusHighlightsActions';
import storeManager from '../../store/StoreManager';
import studentHighlightsService from '../../services/StudentHighlightsService';
import scorableItemNumberingService from '../../services/questionView/ScorableItemNumberingService';
import viewReadyTracker from '../../services/ViewReadyTracker';

import styles from './StimulusDisplay.scss';
import QuestionDisplayContainer from '../question/QuestionDisplay';
import objectTypes from '../../constants/ObjectTypes';
import scrollingService from '../../services/ScrollingService';
import themeServices from '../../themes/ThemeServices';
import navigationService from '../../services/NavigationService';
import SectionRubricBlock from '../../themes/components/fjord/SectionRubricBlock';
import CeqSectionRubricBlock from '../../themes/components/ceq/SectionRubricBlock';
import annotationService from '../../services/AnnotationService';
import UIConstants from '../../constants/UIConstants';
import MathDisplay from '../question/mathDisplay/MathDisplay';
import uiReader from '../../store/readHelpers/uiReader';
import uiService from '../../services/UIService';
import a11yIIBScreenReaderService from '../../services/a11yIIBScreenReaderService';

class StimulusDisplay extends React.PureComponent {
    static propTypes = {
        currentlyShown: PropTypes.object.isRequired,
        customFields: PropTypes.array,
        itemResult: PropTypes.object,
        layoutClassName: PropTypes.string,
        onReady: PropTypes.func.isRequired,
        sectionId: PropTypes.number.isRequired,
        sectionNumber: PropTypes.number,
        sectionTitle: PropTypes.string.isRequired,
        session: PropTypes.object.isRequired,
        settings: PropTypes.object.isRequired,
        stimulusContent: PropTypes.object.isRequired,
        stimulusHighlights: PropTypes.array,
        stimulusHolder: PropTypes.string,
        style: PropTypes.object,
        ui: PropTypes.object.isRequired,
        questionContentRevisionId: PropTypes.number,
    };

    constructor(props) {
        super(props);
        this.highlightsInitialized = false;
    }

    componentDidMount() {
        const { itemResult, sectionId, stimulusContent } = this.props;
        const isDocument = questionReader.isDocument(stimulusContent);
        const isReady = questionReader.isQuestionReady(stimulusContent, itemResult, isDocument, false);

        if (!isReady) {
            assessmentActions.prepareQuestion(stimulusContent, true);
            return;
        }

        MathDisplay.showMath(this.rootElement);
        this.showHighlights();
        themeServices.getThemeInstance().performPostComponentMountActions(`#stimulus-wrapper-${sectionId} :not([id^='question-'])`);
    }

    // eslint-disable-next-line camelcase,react/sort-comp
    UNSAFE_componentWillUpdate(nextProps) {
        if (nextProps.sectionId !== this.props.sectionId) {
            studentHighlightsService.closeTooltip();
        }
    }

    componentDidUpdate(prevProps) {
        const { currentlyShown, itemResult, sectionId, stimulusContent } = this.props;
        const isDocument = questionReader.isDocument(stimulusContent);
        const isReady = questionReader.isQuestionReady(stimulusContent, itemResult, isDocument, false);

        if (prevProps.sectionId !== sectionId) {
            this.highlightsInitialized = false;
            themeServices.getThemeInstance().performPostComponentMountActions(`#stimulus-wrapper-${sectionId} :not([id^='question-'])`);
        }

        if (!isReady) {
            assessmentActions.prepareQuestion(stimulusContent, true);
            return;
        }

        if (this.rootElement) {
            MathDisplay.showMath(this.rootElement);
        }

        this.showHighlights();

        if (
            !navigationService.isOnAllView()
            && stimulusContent.objectType === objectTypes.GAP_MATCH
            && currentlyShown.scorableItem
            && prevProps.currentlyShown.scorableItem !== currentlyShown.scorableItem &&
            stimulusContent.contentRevisionId === currentlyShown.questionId
        ) {
            const scrollToElement = document.getElementById(`scorableItem-${stimulusContent.contentRevisionId}_${currentlyShown.scorableItem}`);
            const scrollToQuestion = document.getElementById(`question-${currentlyShown.questionId}`);
            const firstScorableItemInQuestion = scorableItemNumberingService.getFirstScorableItemInQuestion(stimulusContent.contentRevisionId);
            if (scrollToElement) {
                if (firstScorableItemInQuestion.orderNumber === currentlyShown.scorableItem) {
                    scrollingService.scrollToElement(scrollToQuestion, 300, 16, `stimulus-wrapper-${sectionId}`);
                } else {
                    scrollingService.scrollToElement(scrollToElement, 300, 150, `stimulus-wrapper-${sectionId}`);
                }
            }
        } else if (
            !navigationService.isOnAllView() &&
            prevProps.currentlyShown.sectionId !== currentlyShown.sectionId
        ) {
            const stimulusWrapper = document.getElementById(`stimulus-wrapper-${sectionId}`);
            if (stimulusWrapper) {
                const scrollToElement = stimulusWrapper.firstElementChild;
                scrollingService.scrollToElement(scrollToElement, 0, 16, `stimulus-wrapper-${sectionId}`);
            }
        }
    }

    componentWillUnmount() {
        const { stimulusContent } = this.props;
        if (stimulusContent) {
            responseService.updateItemResultOnNavigation(stimulusContent.contentRevisionId);
        }
        studentHighlightsService.closeTooltip();
    }

    showHighlights() {
        if (!this.highlightsInitialized) {
            this.parseHighlightsInStimulus();
            this.insertStudentHighlights();
            this.highlightsInitialized = true;
        }
    }

    parseHighlightsInStimulus() {
        const { stimulusHighlights = [] } = this.props;
        const highlightsFromDOM = Array.from(
            document.querySelectorAll('[class*="highlight--id-"]')
        );
        const highlightedQuestions = stimulusHighlights;
        highlightsFromDOM.forEach((highlight) => {
            Object.keys(highlight.dataset).forEach((highlightId) => {
                const linkedItemId = parseInt(highlightId.split('linkedItemId-')[1], 10);
                if (highlightedQuestions.indexOf(linkedItemId) < 0) {
                    highlightedQuestions.push(linkedItemId);
                }
            });
        });
        const store = storeManager.getStore();
        store.dispatch(stimulusHighlightsActions.setStimulusHighlights(highlightedQuestions));
    }

    insertStudentHighlights() {
        const { stimulusContent, itemResult } = this.props;
        const studentHighlightsInStimulus = itemResult?.studentHighlights;
        if (Array.isArray(studentHighlightsInStimulus)) {
            studentHighlightsService.applyStudentHighlights(
                studentHighlightsInStimulus,
                stimulusContent.contentRevisionId
            );
        }
    }

    handleUserResponse = (...args) => {
        responseService.handleUserResponse(...args);
    };

    getAriaLabel = () => {
        const {
            currentlyShown,
            sectionTitle,
            stimulusHolder,
        } = this.props;

        const ariaTitle = uiService.isQuestionStimulusHolder(stimulusHolder)
            ? scorableItemNumberingService.getQuestionTitle(currentlyShown.questionId)
            : sectionTitle;

        return `${t('generic.stimulusForPrefix')} ${ariaTitle}`;
    };

    getStimulusLabel = (sectionNumber, stimulusHolder) => {
        if (settingsReader.useTestSequential() || uiService.isQuestionStimulusHolder(stimulusHolder)) {
            return null;
        } else if (sectionNumber === -1) {
            return <i className="fa fa-info-circle" aria-hidden="true" />;
        }

        return sectionNumber;
    };

    setAnnotatableQuestion = ({ target }) => {
        const { stimulusContent: { contentRevisionId, category, objectType, questionId } } = this.props;

        let questionType = category || objectTypes.CATEGORY.QUESTION;
        if (questionType === objectTypes.CATEGORY.STIMULUS && target.closest(`#${UIConstants.GAP_MATCH_SELECTOR_ID}`)) {
            questionType = objectTypes.CATEGORY.QUESTION;
        }

        // if this is question stimulus then use question id (stimulus belongs to that question) instead of contentRevisionId
        const annotatingContentId = objectType === objectTypes.STIMULUS ? questionId : contentRevisionId;
        return annotationService.setAnnotatingContent(annotatingContentId, questionType);
    };

    render() {
        const {
            customFields,
            itemResult,
            layoutClassName,
            sectionId,
            sectionNumber,
            session,
            settings,
            stimulusContent,
            stimulusHolder,
            style,
            ui,
            currentlyShown,
            questionContentRevisionId,
        } = this.props;

        const userResponses = {};
        const choiceSequences = {};
        const isReadOnly = !sessionReader.isInteractive();
        const { contentRevisionId } = stimulusContent;
        const interactionSupportsRTL = uiReader.interactionSupportsRTL(stimulusContent.objectType);
        const isDocument = questionReader.isDocument(stimulusContent);
        const isReady = questionReader.isQuestionReady(stimulusContent, itemResult, isDocument, false);
        const currentlyShownQuestion = currentlyShown?.questionId ?
            assessmentReader.getQuestion(currentlyShown.questionId) : null;

        if (!isReady) {
            return null;
        }

        if (itemResult) {
            itemResult.responseIdentifiers.forEach((responseIdentifier) => {
                userResponses[responseIdentifier.identifier] = responseIdentifier.userResponse;
                choiceSequences[responseIdentifier.identifier] = responseIdentifier.choiceSequence;
            });
        }

        const classNamesWrapper = [styles.sectionStimulusWrapper, UIConstants.GAP_MATCH_SCROLL_CLASS];
        const classNames = [styles.sectionStimulus];
        const contentClasses = [styles.stimulusContent];
        const showStimulusMargins = uiReader.showStimulusMargins();

        if (sessionReader.isViewMediaPrint() || sessionReader.isViewMediaPdf()) {
            classNamesWrapper.push('print-stimulus');
            if (settingsReader.hasSettingValue(sessionReader.printParamNames.INSERT_PAGE_BREAK_BEFORE_QUESTION, true)) {
                classNamesWrapper.push('page-break-before');
            }
        }

        if (!showStimulusMargins) {
            contentClasses.push('no-margins');
        }

        const shouldHideResponseAreas = settingsReader.shouldHideResponseAreasForPaperSubmissions(currentlyShownQuestion);
        const shouldHideResponseAreaInDemo = settingsReader.shouldHideResponseAreaInDemoTestForPaperSubmissions(currentlyShownQuestion);
        if (currentlyShownQuestion && (shouldHideResponseAreas || shouldHideResponseAreaInDemo)) {
            classNames.push('align-stimulus-left');
        }

        if (sessionReader.isPrintStylePaperBased()) {
            classNamesWrapper.push('paper-based-stimulus-wrapper');
            contentClasses.push('paper-based-stimulus');
            uiService.applyImageFilterForPaperBased();
            const layoutClass = themeServices.getThemeInstance().getStimulusLayoutClassSelector(customFields);

            if (layoutClass) {
                contentClasses.push(`${layoutClass}`);
            }
            if (
                typeof questionContentRevisionId === 'number' &&
                assessmentReader.getQuestion(questionContentRevisionId)?.objectType === objectTypes.EXTENDED_TEXT
            ) {
                classNames.push(styles.stimulusContentWrapperET);
            }
        }

        const setAnnotatableQuestionEvent = studentHighlightsService.isNoteTakingAndHighlightingEnabled() ? this.setAnnotatableQuestion : undefined;
        const shouldAllowScreenReaderFocus = shouldHideResponseAreas || shouldHideResponseAreaInDemo;
        const tabIndexValue = (themeServices.isIelts() && shouldAllowScreenReaderFocus) ? 0 : a11yIIBScreenReaderService.getTabIndexValue(undefined);

        return (
            <div // eslint-disable-line jsx-a11y/no-static-element-interactions
                className={classNamesWrapper.join(' ')}
                id={`stimulus-wrapper-${sectionId}`}
                lang={sessionReader.getLanguage()}
                dir={ui.direction}
                style={style}
                ref={(element) => { this.rootElement = element; }}
                onMouseDown={setAnnotatableQuestionEvent}
                onTouchStart={setAnnotatableQuestionEvent}
                {...uiService.getQuestionRegionsAccessibilityAttributes()}
            >
                { uiService.areQuestionSectionsFocusable() &&
                    <span className="sr-only">
                        { this.getAriaLabel() }
                    </span>
                }

                <div className={classNames.join(' ')} ref={(element) => { this.stimulus = element; }}>
                    { showStimulusMargins &&
                        <div className={styles.stimulusMargin}>
                            {this.getStimulusLabel(sectionNumber, stimulusHolder)}
                        </div>
                    }

                    <div className={contentClasses.join(' ')} id={`stimulus-content-${contentRevisionId}`} tabIndex={tabIndexValue}>
                        {
                            stimulusContent.objectType === objectTypes.GAP_MATCH
                                ? <QuestionDisplayContainer
                                    question={stimulusContent}
                                    focusOnLoad={false}
                                    onReady={this.props.onReady}
                                    key={stimulusContent.contentRevisionId}
                                />
                                : <QTIAssessmentItem
                                    callback={this.handleUserResponse}
                                    choiceSequences={choiceSequences}
                                    context={session.context}
                                    reportReady={this.props.onReady}
                                    isReadOnly={isReadOnly}
                                    key={contentRevisionId}
                                    question={stimulusContent}
                                    services={questionHelperServices.getServices()}
                                    userResponses={userResponses}
                                    settingValues={settings.values}
                                    viewMedia={session.viewMedia}
                                    direction={interactionSupportsRTL ? ui.direction : 'ltr'}
                                />
                        }
                    </div>

                    { showStimulusMargins && <div className={styles.stimulusMargin} /> }
                </div>

                { uiReader.showQuestionCopyrightText() && layoutClassName === 'left-stimulus' &&
                    <div className={styles.copyright}>
                        {i18nService.translate(`question.copyright.${ui.marketplaceTheme}`)}
                    </div>
                }
            </div>
        );
    }
}

class StimulusDisplayWithRubric extends React.PureComponent {
    static propTypes = {
        customFields: PropTypes.array,
        ui: PropTypes.object.isRequired,
        section: PropTypes.object.isRequired,
        questionContentRevisionId: PropTypes.number,
    };

    render() {
        const { customFields, section, ui, questionContentRevisionId } = this.props;
        const isViewPrint = sessionReader.isViewMediaPrintOrPdf();
        const isPaperBased = sessionReader.isPrintStylePaperBased();
        const isMppOnForCEQPaperBased = themeServices.getThemeInstance().shouldRenderPaperBasedRubric();
        const showSectionRubricBlock = isViewPrint && uiReader.showSectionRubric() && customFields && !isMppOnForCEQPaperBased;
        const isFirstQuestion = (section?.questions?.[0]?.contentRevisionId === questionContentRevisionId);
        return (
            <React.Fragment>
                { showSectionRubricBlock &&
                    <SectionRubricBlock
                        section={section}
                        ui={ui}
                        isPaperBased={isPaperBased}
                    />
                }
                {
                    isPaperBased && isMppOnForCEQPaperBased && (isFirstQuestion || questionContentRevisionId == null) &&
                    <CeqSectionRubricBlock
                        section={section}
                        ui={ui}
                    />
                }
                <StimulusDisplay {...this.props} />
            </React.Fragment>
        );
    }
}


export function mapStateToProps(state, ownProps) {
    const { currentlyShown, ui, response, settings, session } = state;
    let { section } = ownProps;
    const questionContentRevisionId = ownProps?.questionContentRevisionId;
    let stimulusContent;

    if (section === undefined) {
        const sectionId = ownProps.sectionId || state.currentlyShown.sectionId;
        section = assessmentReader.getSectionById(sectionId);
    }

    const sectionNumber = section.questions && section.questions.length ? section.questions[0].sectionNumber : null;

    if (questionContentRevisionId && uiService.isPbAndMultipleStimulusEnabled() && section?.questions) {
        const filteredQuestion = section.questions.find(question => question.contentRevisionId === questionContentRevisionId);
        stimulusContent = filteredQuestion?.stimulus;
    } else {
        stimulusContent = section.stimulusContent;
    }

    return {
        currentlyShown,
        customFields: section.customFields,
        itemResult: response[stimulusContent?.contentRevisionId],
        onReady: ownProps.onReady || viewReadyTracker.getReporter(),
        section,
        sectionId: section.id,
        sectionNumber,
        sectionTitle: section.title,
        session,
        settings,
        stimulusContent,
        ui,
    };
}

export const StimulusDisplayContainer = connect(mapStateToProps)(StimulusDisplayWithRubric);
export default StimulusDisplay;
